import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, MenuItem, Button, IconButton, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TableSortLabel,
  TablePagination, InputAdornment, LinearProgress, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert,
  Switch, FormControlLabel,
} from "@mui/material";
import { FaSearch, FaTrashAlt, FaRedo, FaArrowLeft, FaFilePdf, FaCog, FaPlus, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../Api";
import { addDaysToDateString, getAppDateString } from "../utils/date";
import { hasAnyAuthRole } from "../auth";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const daysAgo = (n) => addDaysToDateString(getAppDateString(), -n);
const blankAccount = { key: "", label: "", advertiser: "", address: "", pan: "", gstin: "", contact_name: "", phone: "", email: "", signature_name: "", prefix: "", gst_rate: 18, validity_months: 2, sort_order: 0, is_active: true };
const apiError = (err, fallback) => err?.response?.data?.error || fallback;

export default function MyntraAdvPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const canManage = hasAnyAuthRole(["admin", "super_admin"]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAccounts, setSettingsAccounts] = useState([]);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [newAccount, setNewAccount] = useState(blankAccount);

  // ---- export form ----
  const [account, setAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastMsg, setLastMsg] = useState(null);

  // ---- history filters ----
  const [from, setFrom] = useState(daysAgo(90));
  const [to, setTo] = useState(getAppDateString());
  const [hAccount, setHAccount] = useState("");
  const [search, setSearch] = useState("");
  const [dSearch, setDSearch] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/api/myntra-adv/accounts");
    setAccounts(data.accounts || []);
  }, []);

  useEffect(() => { loadAccounts().catch(() => {}); }, [loadAccounts]);

  useEffect(() => {
    const t = setTimeout(() => { setDSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/myntra-adv/history", {
        params: { from, to, account: hAccount, search: dSearch, sort: sort.key, dir: sort.dir, page: page + 1, pageSize },
      });
      setData(data);
    } catch {
      toast.error("Could not load history.");
      setData({ rows: [], total: 0, byAccount: [], grand: { count: 0, amount: 0, gst: 0, total: 0 } });
    } finally {
      setLoading(false);
    }
  }, [from, to, hAccount, dSearch, sort, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // ---- export ----
  const amt = Number(amount);
  const acctObj = accounts.find((a) => a.key === account);
  const preview = useMemo(() => {
    if (!Number.isFinite(amt) || amt <= 0) return null;
    const gstRate = Number(acctObj?.gst_rate ?? 18);
    const gst = Math.round(amt * gstRate / 100 * 100) / 100;
    return { gst, total: Math.round((amt + gst) * 100) / 100 };
  }, [amt, acctObj]);

  const exportPdf = async () => {
    if (!account) { toast.warn("Pick an account."); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast.warn("Enter an amount greater than 0."); return; }
    setBusy(true);
    setLastMsg(null);
    try {
      const resp = await api.post("/api/myntra-adv/export", { account, amount: amt }, { responseType: "blob" });
      const ro = resp.headers["x-adv-ro"] || "RO";
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${ro}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setLastMsg({ t: "success", m: `Exported ${ro}.pdf (${inr(preview?.total)}) — saved to your Downloads.` });
      toast.success(`${ro}.pdf downloaded.`);
      setAmount("");
      load();   // refresh the history below
    } catch (err) {
      let m = "Export failed on the server.";
      try { m = JSON.parse(await err.response.data.text()).error || m; } catch (_) {}
      setLastMsg({ t: "error", m });
      toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  // ---- history helpers ----
  const setSortKey = (key) => { setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" })); setPage(0); };
  const setPreset = (days) => { setFrom(daysAgo(days)); setTo(getAppDateString()); setPage(0); };
  const doDelete = async (row) => {
    setDeletingId(row.id);
    try {
      await api.delete(`/api/myntra-adv/history/${row.id}`);
      toast.success("Entry deleted.");
      setConfirmRow(null);
      load();
    } catch {
      toast.error("Could not delete entry.");
    } finally {
      setDeletingId(null);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    setSettingsBusy(true);
    try {
      const { data } = await api.get("/api/myntra-adv/settings/accounts");
      setSettingsAccounts(data.accounts || []);
    } catch (err) {
      toast.error(apiError(err, "Could not load ADV settings."));
    } finally {
      setSettingsBusy(false);
    }
  };

  const updateSettingsRow = (id, field, value) => {
    setSettingsAccounts((rows) => rows.map((row) => row.id === id ? { ...row, [field]: value } : row));
  };

  const saveSettingsRow = async (row) => {
    setSettingsBusy(true);
    try {
      await api.put(`/api/myntra-adv/settings/accounts/${row.id}`, row);
      toast.success(`${row.label} saved.`);
      await Promise.all([openSettings(), loadAccounts()]);
    } catch (err) {
      toast.error(apiError(err, "Could not save account."));
    } finally {
      setSettingsBusy(false);
    }
  };

  const addSettingsAccount = async () => {
    setSettingsBusy(true);
    try {
      await api.post("/api/myntra-adv/settings/accounts", newAccount);
      toast.success("ADV account added.");
      setNewAccount(blankAccount);
      await Promise.all([openSettings(), loadAccounts()]);
    } catch (err) {
      toast.error(apiError(err, "Could not add account."));
    } finally {
      setSettingsBusy(false);
    }
  };

  const rows = data?.rows || [];
  const grand = data?.grand || { count: 0, amount: 0, gst: 0, total: 0 };
  const byAccount = data?.byAccount || [];
  const headCells = [
    ["created_at", "Date", "left"], ["account", "Account", "left"], ["ro_number", "RO Number", "left"],
    ["amount", "Amount", "right"], ["gst", "GST", "right"], ["total", "Total", "right"],
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Button startIcon={<FaArrowLeft />} onClick={() => navigate("/admin/myntra-automation")}>Back</Button>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>🧾 Myntra ADV — RO/PO</Typography>
        <Box sx={{ flex: 1 }} />
        {canManage && (
          <Tooltip title="ADV settings">
            <IconButton color="primary" onClick={openSettings} aria-label="ADV settings"><FaCog /></IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ===== Export ===== */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Export a Release-Order (RO/PO)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick an account and enter the pre-tax amount. The server fills the RO number, dates and configured GST,
          then downloads the PDF to your Downloads folder.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField select label="Account" size="small" value={account} disabled={busy} sx={{ minWidth: 260 }}
            onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <MenuItem key={a.key} value={a.key}>{a.label} — {a.advertiser} ({a.prefix})</MenuItem>)}
          </TextField>
          <TextField label="Amount (In INR, pre-tax)" type="number" size="small" value={amount} disabled={busy}
            onChange={(e) => setAmount(e.target.value)} InputProps={{ inputProps: { min: 0, step: "any" } }} sx={{ minWidth: 200 }} />
          <Button variant="contained" startIcon={<FaFilePdf />} onClick={exportPdf}
            disabled={busy || !account || !preview || !acctObj?.template_present}>{busy ? "Exporting…" : "Export PDF"}</Button>
          {preview && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Chip variant="outlined" size="small" label={`Amount ${inr(amt)}`} />
              <Chip variant="outlined" size="small" label={`GST ${Number(acctObj?.gst_rate ?? 18)}% ${inr(preview.gst)}`} />
              <Chip color="success" size="small" label={`Total ${inr(preview.total)}`} />
              {acctObj && <Chip color="primary" variant="outlined" size="small" label={`prefix ${acctObj.prefix}`} />}
              {acctObj && <Chip variant="outlined" size="small" label={`${acctObj.validity_months} month validity`} />}
            </Box>
          )}
        </Box>
        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {lastMsg && <Alert severity={lastMsg.t} sx={{ mt: 2 }}>{lastMsg.m}</Alert>}
        {acctObj && !acctObj.template_present && (
          <Alert severity="warning" sx={{ mt: 2 }}>Shared ADV template is missing on the server.</Alert>
        )}
      </Paper>

      {/* ===== History filters ===== */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Export history</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FaRedo />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField label="From" type="date" size="small" value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to}
            onChange={(e) => { setTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {[[30, "30d"], [90, "90d"], [180, "6m"], [365, "1y"]].map(([d, lbl]) => (
              <Button key={d} size="small" variant="outlined" onClick={() => setPreset(d)}>{lbl}</Button>
            ))}
          </Box>
          <TextField select label="Account" size="small" value={hAccount} sx={{ minWidth: 190 }}
            onChange={(e) => { setHAccount(e.target.value); setPage(0); }}>
            <MenuItem value="">All accounts</MenuItem>
            {accounts.map((a) => <MenuItem key={a.key} value={a.key}>{a.advertiser} ({a.prefix})</MenuItem>)}
          </TextField>
          <TextField size="small" placeholder="Search RO / amount / user…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment> }} />
        </Box>
      </Paper>

      {/* Account-wise totals */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <Paper sx={{ p: 1.5, height: "100%", bgcolor: "success.main", color: "#fff" }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>ALL ACCOUNTS · {grand.count} ROs</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{inr(grand.total)}</Typography>
            <Typography variant="caption">amt {inr(grand.amount)} · gst {inr(grand.gst)}</Typography>
          </Paper>
        </Grid>
        {byAccount.map((a) => (
          <Grid item xs={12} sm={6} md={3} lg={2} key={a.account}>
            <Paper sx={{ p: 1.5, height: "100%" }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {a.advertiser} · {a.count} ROs
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{inr(a.total)}</Typography>
              <Typography variant="caption" color="text.secondary">amt {inr(a.amount)} · gst {inr(a.gst)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: "52vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map(([key, label, align]) => (
                  <TableCell key={key} align={align} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : "asc"}
                      onClick={() => setSortKey(key)}>{label}</TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>By</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Del</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading ? (
                <TableRow><TableCell colSpan={9}><Typography color="text.secondary" sx={{ p: 2 }}>No entries match your filters.</Typography></TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.created_at}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.advertiser}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.ro_number}</TableCell>
                  <TableCell align="right">{inr(r.amount)}</TableCell>
                  <TableCell align="right">{inr(r.gst)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(r.total)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.ro_start} → {r.ro_end}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.created_by || "—"}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete entry">
                      <span>
                        <IconButton size="small" color="error" disabled={deletingId === r.id}
                          onClick={() => setConfirmRow(r)}><FaTrashAlt size={13} /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={data?.total || 0}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
        />
      </Paper>

      <Dialog open={settingsOpen} onClose={() => !settingsBusy && setSettingsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>ADV settings</DialogTitle>
        <DialogContent dividers>
          {settingsBusy && <LinearProgress sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Accounts</Typography>
          {settingsAccounts.map((row) => (
            <Box key={row.id} sx={{ py: 1.5 }}>
              <Grid container spacing={1.25} alignItems="center">
                <Grid item xs={12} sm={2}><TextField fullWidth size="small" label="Label" value={row.label}
                  onChange={(e) => updateSettingsRow(row.id, "label", e.target.value)} /></Grid>
                <Grid item xs={12} sm={2.4}><TextField fullWidth size="small" label="Advertiser" value={row.advertiser}
                  onChange={(e) => updateSettingsRow(row.id, "advertiser", e.target.value)} /></Grid>
                <Grid item xs={6} sm={1}><TextField fullWidth size="small" label="Prefix" value={row.prefix}
                  onChange={(e) => updateSettingsRow(row.id, "prefix", e.target.value.toUpperCase())} /></Grid>
                <Grid item xs={6} sm={1.1}><TextField fullWidth size="small" type="number" label="GST %" value={row.gst_rate}
                  onChange={(e) => updateSettingsRow(row.id, "gst_rate", e.target.value)} /></Grid>
                <Grid item xs={6} sm={1.2}><TextField fullWidth size="small" type="number" label="Months" value={row.validity_months}
                  onChange={(e) => updateSettingsRow(row.id, "validity_months", e.target.value)} /></Grid>
                <Grid item xs={6} sm={1}><TextField fullWidth size="small" type="number" label="Order" value={row.sort_order}
                  onChange={(e) => updateSettingsRow(row.id, "sort_order", e.target.value)} /></Grid>
                <Grid item xs={12} sm={1.3}><FormControlLabel control={<Switch checked={row.is_active}
                  onChange={(e) => updateSettingsRow(row.id, "is_active", e.target.checked)} />} label="Active" /></Grid>
                <Grid item xs={12} sm={0.7} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Tooltip title="Save account"><span><IconButton color="primary" disabled={settingsBusy}
                    onClick={() => saveSettingsRow(row)}><FaSave /></IconButton></span></Tooltip>
                </Grid>
              </Grid>
              <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
                <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Registered address" value={row.address || ""}
                  onChange={(e) => updateSettingsRow(row.id, "address", e.target.value)} /></Grid>
                <Grid item xs={6} md={3}><TextField fullWidth size="small" label="PAN" value={row.pan || ""}
                  onChange={(e) => updateSettingsRow(row.id, "pan", e.target.value.toUpperCase())} /></Grid>
                <Grid item xs={6} md={3}><TextField fullWidth size="small" label="GSTIN" value={row.gstin || ""}
                  onChange={(e) => updateSettingsRow(row.id, "gstin", e.target.value.toUpperCase())} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Authorized person" value={row.contact_name || ""}
                  onChange={(e) => updateSettingsRow(row.id, "contact_name", e.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={2}><TextField fullWidth size="small" label="Phone" value={row.phone || ""}
                  onChange={(e) => updateSettingsRow(row.id, "phone", e.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" label="Email" value={row.email || ""}
                  onChange={(e) => updateSettingsRow(row.id, "email", e.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Signature name" value={row.signature_name || ""}
                  onChange={(e) => updateSettingsRow(row.id, "signature_name", e.target.value)} /></Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary">Key: {row.key} · Shared template: {row.template_filename}</Typography>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Add account</Typography>
          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs={12} sm={1.3}><TextField fullWidth size="small" label="Key" value={newAccount.key}
              onChange={(e) => setNewAccount((v) => ({ ...v, key: e.target.value.toLowerCase() }))} /></Grid>
            <Grid item xs={12} sm={2}><TextField fullWidth size="small" label="Label" value={newAccount.label}
              onChange={(e) => setNewAccount((v) => ({ ...v, label: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={2.4}><TextField fullWidth size="small" label="Advertiser" value={newAccount.advertiser}
              onChange={(e) => setNewAccount((v) => ({ ...v, advertiser: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={1}><TextField fullWidth size="small" label="Prefix" value={newAccount.prefix}
              onChange={(e) => setNewAccount((v) => ({ ...v, prefix: e.target.value.toUpperCase() }))} /></Grid>
            <Grid item xs={6} sm={1.1}><TextField fullWidth size="small" type="number" label="GST %" value={newAccount.gst_rate}
              onChange={(e) => setNewAccount((v) => ({ ...v, gst_rate: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={1.2}><TextField fullWidth size="small" type="number" label="Months" value={newAccount.validity_months}
              onChange={(e) => setNewAccount((v) => ({ ...v, validity_months: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={1}><TextField fullWidth size="small" type="number" label="Order" value={newAccount.sort_order}
              onChange={(e) => setNewAccount((v) => ({ ...v, sort_order: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={2}><Button fullWidth variant="contained" startIcon={<FaPlus />} disabled={settingsBusy}
              onClick={addSettingsAccount}>Add</Button></Grid>
          </Grid>
          <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Registered address" value={newAccount.address}
              onChange={(e) => setNewAccount((v) => ({ ...v, address: e.target.value }))} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="PAN" value={newAccount.pan}
              onChange={(e) => setNewAccount((v) => ({ ...v, pan: e.target.value.toUpperCase() }))} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="GSTIN" value={newAccount.gstin}
              onChange={(e) => setNewAccount((v) => ({ ...v, gstin: e.target.value.toUpperCase() }))} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Authorized person" value={newAccount.contact_name}
              onChange={(e) => setNewAccount((v) => ({ ...v, contact_name: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField fullWidth size="small" label="Phone" value={newAccount.phone}
              onChange={(e) => setNewAccount((v) => ({ ...v, phone: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" label="Email" value={newAccount.email}
              onChange={(e) => setNewAccount((v) => ({ ...v, email: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Signature name" value={newAccount.signature_name}
              onChange={(e) => setNewAccount((v) => ({ ...v, signature_name: e.target.value }))} /></Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 1.5 }}>All accounts use the same shared template; only the account details above change.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={() => setSettingsOpen(false)} disabled={settingsBusy}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirmRow} onClose={() => setConfirmRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete history entry?</DialogTitle>
        <DialogContent dividers>
          {confirmRow && (
            <Typography variant="body2">
              Remove <b>{confirmRow.ro_number}</b> — {confirmRow.advertiser}, {inr(confirmRow.total)} ({confirmRow.created_at})?
              This only deletes the history record; any downloaded PDF is unaffected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)} disabled={deletingId != null}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<FaTrashAlt />} disabled={deletingId != null}
            onClick={() => confirmRow && doDelete(confirmRow)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
